// src/hooks/usePwaPrompt.js
import { useEffect, useState } from "react";

const STORAGE_KEY = "pwaPromptLastShown";
const COOLDOWN_MS = 24 * 60 * 60 * 1000; // 1 day in ms

export const usePwaPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isInstallable, setIsInstallable] = useState(false);

  useEffect(() => {
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setIsInstallable(true);

      try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        const lastShown = raw ? parseInt(raw, 10) : 0;
        const now = Date.now();

        // Auto show only if never shown, or last shown more than COOLDOWN_MS ago
        if (!lastShown || now - lastShown >= COOLDOWN_MS) {
          setShowPrompt(true);
          window.localStorage.setItem(STORAGE_KEY, String(now));
        }
      } catch (err) {
        console.warn("Failed to read/write PWA prompt cooldown:", err);
        // Fallback, still show the prompt once
        setShowPrompt(true);
      }
    };

    const handleAppInstalled = () => {
      // App is installed, no more prompts
      setShowPrompt(false);
      setIsInstallable(false);
      setDeferredPrompt(null);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    window.addEventListener("appinstalled", handleAppInstalled);

    return () => {
      window.removeEventListener(
        "beforeinstallprompt",
        handleBeforeInstallPrompt
      );
      window.removeEventListener("appinstalled", handleAppInstalled);
    };
  }, []);

  const triggerPrompt = async () => {
    if (!deferredPrompt) {
      return { prompted: false, outcome: "unavailable" };
    }

    try {
      deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;
      if (choice.outcome === "accepted") {
        console.log("PWA install accepted");
      } else {
        console.log("PWA install dismissed");
      }
      return { prompted: true, outcome: choice.outcome };
    } catch (err) {
      console.warn("Error triggering PWA install prompt:", err);
      return { prompted: false, outcome: "error", error: err };
    } finally {
      setShowPrompt(false);
      setDeferredPrompt(null);
      setIsInstallable(false);
    }
  };

  return { showPrompt, triggerPrompt, isInstallable };
};
